import { useApi, useApiRef } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { haversineDistance } from '@/components/Functional/utils/calcGeoLocationDistance'
import { Snackbar } from '@/components/UI/Snackbar'
import type { GeofenceAreaData } from '@/schemas/v2/geofence'
import { useTokenStore } from '@/store/useTokenStore'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

type AreaState = { entered: boolean; exited: boolean }
type LatLng = { latitude: number; longitude: number }

const lsKey = (slug: string) => `${slug}-geofence`

/** localStorage から全エリアの入退室状態を読む（campaign スコープ）。 */
function readLsAll(slug: string): Record<string, AreaState> {
  try {
    const raw = localStorage.getItem(lsKey(slug))
    return raw ? (JSON.parse(raw) as Record<string, AreaState>) : {}
  } catch {
    return {}
  }
}

/** localStorage に指定エリアの入退室状態を書く。 */
function writeLs(slug: string, areaId: number, state: AreaState): void {
  try {
    const all = readLsAll(slug)
    all[String(areaId)] = state
    localStorage.setItem(lsKey(slug), JSON.stringify(all))
  } catch {
    // localStorage 不可（プライベートモード等）は無視
  }
}

/**
 * 入退室メッセージ（Geofence / #3259）。
 * 認証済みユーザーに対し `GET /api/v2/geofence-areas` の判定エリアを取得し、GPS を監視して
 * 入室／退出を検知したら該当メッセージを Snackbar 表示し、`POST .../events` で表示履歴を記録する。
 *
 * 入室状態は「サーバの entered」と「localStorage」の両方で保持する。退出は
 * 「入室済み（entered=true）かつ退室未表示（exited=false）で、現在エリア外」で発火する。
 * これにより、内→外の遷移を観測できなかった場合（アプリ再訪・リロード）でも、
 * localStorage もしくは API の entered=true を根拠に退出メッセージを表示できる。
 */
export function GeofenceMessage() {
  const api = useApi()
  const apiRef = useApiRef()
  const { campaignSlug } = useCampaignSlug()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null

  const { data } = useQuery({
    queryKey: ['geofenceAreas', campaignSlug],
    queryFn: () => api.auth.fetchGeofenceAreas(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  })

  // active=false のときは areas が必ず空。監視対象なし
  const areas = useMemo<GeofenceAreaData[]>(() => (data?.active ? data.areas : []), [data])

  // 表示済み状態（サーバ entered/exited と localStorage をマージした値を保持し、イベント記録で更新）
  const stateRef = useRef<Map<number, AreaState>>(new Map())

  const [message, setMessage] = useState<{ title: string; body: string } | null>(null)

  useEffect(() => {
    if (areas.length === 0 || !('geolocation' in navigator)) return

    // 初期状態: サーバ値 ∪ localStorage。
    // ただしサーバが entered=false かつ exited=false（この会期は未入室・未退室の基準状態）なら、
    // 前会期の古い localStorage を破棄してリセットする。
    const lsAll = readLsAll(campaignSlug)
    const states = new Map<number, AreaState>()
    for (const a of areas) {
      const ls = lsAll[String(a.id)]
      const st: AreaState =
        !a.entered && !a.exited
          ? { entered: false, exited: false }
          : { entered: a.entered || (ls?.entered ?? false), exited: a.exited || (ls?.exited ?? false) }
      states.set(a.id, st)
      writeLs(campaignSlug, a.id, st)
    }
    stateRef.current = states

    const record = async (area: GeofenceAreaData, type: 'enter' | 'exit', cur: LatLng) => {
      const st = stateRef.current.get(area.id) ?? { entered: false, exited: false }
      // 多重表示防止のため楽観的に「表示済み」へ更新し、localStorage にも永続化
      const optimistic: AreaState = type === 'enter' ? { ...st, entered: true } : { ...st, exited: true }
      stateRef.current.set(area.id, optimistic)
      writeLs(campaignSlug, area.id, optimistic)
      try {
        const res = await apiRef.current.auth.recordGeofenceEvent(area.id, {
          type,
          latitude: cur.latitude,
          longitude: cur.longitude,
        })
        const merged: AreaState = {
          entered: optimistic.entered || res.entered,
          exited: optimistic.exited || res.exited,
        }
        stateRef.current.set(area.id, merged)
        writeLs(campaignSlug, area.id, merged)
      } catch {
        // 記録失敗時も楽観的な「表示済み」を維持して再表示を防ぐ
      }
    }

    const handle = (pos: GeolocationPosition) => {
      const cur: LatLng = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
      for (const area of areas) {
        const inside = haversineDistance(cur, { latitude: area.latitude, longitude: area.longitude }) <= area.radius
        const st = stateRef.current.get(area.id) ?? { entered: false, exited: false }

        if (inside && !st.entered) {
          // 入室: エリア内 && 入室未表示
          void record(area, 'enter', cur)
          setMessage({ title: area.enterTitle, body: area.enterBody })
        } else if (!inside && st.entered && !st.exited) {
          // 退室: 入室済み && 退室未表示 && 現在エリア外（遷移観測に依存せず localStorage/API entered を根拠に発火）
          void record(area, 'exit', cur)
          setMessage({ title: area.exitTitle, body: area.exitBody })
        }
      }
    }

    const watchId = navigator.geolocation.watchPosition(handle, () => {}, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
    })
    return () => navigator.geolocation.clearWatch(watchId)
  }, [areas, apiRef, campaignSlug])

  if (!message) return null

  return (
    <div className="fixed inset-x-0 bottom-[88px] z-50 flex justify-center px-4">
      <Snackbar title={message.title} message={message.body} onClose={() => setMessage(null)} />
    </div>
  )
}
